import React, { useState, useEffect, useRef } from 'react';
import { Product } from '../db';
import { productsAPI } from '../backend';
import { Edit, Trash2, Package, Plus, Upload, X, Tag } from 'lucide-react';
import ProductImage from './ProductImage';
import BrandsView from './BrandsView';
import { brandsOf, NO_BRAND } from '../utils/brands';

function ProductManagement() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'list' | 'new' | 'edit' | 'brands'>('list');
  const [productEdit, setProductEdit] = useState<Product | null>(null);
  const [form, setForm] = useState<Omit<Product, 'id'>>({
    name: '',
    image: '',
    stock: 0,
    price: 0,
    cost: 0,
    brand: ''
  });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [brandFilter, setBrandFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productsAPI.getAll();
      setProducts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setForm({ name: '', image: '', stock: 0, price: 0, cost: 0, brand: '' });
    setImagePreview(null);
    setImageFile(null);
    setMode('new');
  };

  const handleEdit = (product: Product) => {
    setProductEdit(product);
    setForm({
      name: product.name,
      image: product.image,
      stock: product.stock,
      price: product.price,
      cost: product.cost,
      brand: product.brand ?? ''
    });
    setImagePreview(product.image || null);
    setImageFile(null);
    setMode('edit');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagePreview(URL.createObjectURL(file));
    setImageFile(file);
  };

  const handleRemoveImage = () => {
    setForm(f => ({ ...f, image: '' }));
    setImagePreview(null);
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Seguro que deseas eliminar este producto?')) {
      try {
        await productsAPI.delete(id);
        await loadProducts();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error deleting product');
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (mode === 'new') {
        await productsAPI.create(form, imageFile ?? undefined);
      } else if (mode === 'edit' && productEdit) {
        await productsAPI.update(productEdit.id, form, imageFile ?? undefined);
      }
      await loadProducts();
      setMode('list');
      setProductEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading products...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  if (mode === 'new' || mode === 'edit') {
    return (
      <div className="main-view">
        <div className="products-header">
          <h1>{mode === 'new' ? 'Nuevo Producto' : 'Editar Producto'}</h1>
          <p>{mode === 'new' ? 'Agrega un nuevo producto al catálogo' : 'Modifica los datos del producto'}</p>
        </div>

        <div className="product-form-container">
          <div className="product-form-card">
            <form className="product-form" onSubmit={handleSave}>
              <div className="form-group">
                <label htmlFor="name" className="required">Nombre del producto</label>
                <input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ingresa el nombre del producto"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="brand">Marca</label>
                <input
                  id="brand"
                  type="text"
                  list="brand-options"
                  value={form.brand ?? ''}
                  onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                  placeholder="Sin marca"
                  className="form-input"
                />
                <datalist id="brand-options">
                  {brandsOf(products).map(b => <option key={b} value={b} />)}
                </datalist>
              </div>

              <div className="form-group">
                <label>Imagen del producto</label>
                <div className="image-upload-area" onClick={() => fileInputRef.current?.click()}>
                  {imagePreview ? (
                    <div className="image-upload-preview">
                      <img src={imagePreview} alt="Preview" className="image-preview-img" />
                      <button
                        type="button"
                        className="image-remove-btn"
                        onClick={e => { e.stopPropagation(); handleRemoveImage(); }}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="image-upload-placeholder">
                      <Upload size={24} />
                      <span>Toca para subir una imagen</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleImageChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="stock" className="required">Stock disponible</label>
                <input
                  id="stock"
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={e => setForm(f => ({ ...f, stock: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="cost" className="required">Costo</label>
                <input
                  id="cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.cost}
                  onChange={e => setForm(f => ({ ...f, cost: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="price" className="required">Precio de venta</label>
                <input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-actions">
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => { setMode('list'); setProductEdit(null); }}
                >
                  Cancelar
                </button>
                {mode === 'edit' && productEdit && (
                  <button
                    className="btn-secondary btn-danger"
                    type="button"
                    onClick={() => handleDelete(productEdit.id)}
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </button>
                )}
                <button className="btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Guardando...' : mode === 'new' ? 'Crear Producto' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'brands') {
    return (
      <BrandsView
        products={products}
        onBack={() => setMode('list')}
        canRename
        onChanged={async () => { setBrandFilter(''); await loadProducts(); }}
      />
    );
  }

  const brands = brandsOf(products);
  const term = searchTerm.trim().toLowerCase();
  const visibleProducts = products.filter(product => {
    const matchesBrand = !brandFilter
      || (brandFilter === NO_BRAND ? !product.brand : product.brand === brandFilter);
    const matchesSearch = !term
      || product.name.toLowerCase().includes(term)
      || (product.brand || '').toLowerCase().includes(term);
    return matchesBrand && matchesSearch;
  });

  return (
    <div className="main-view">
      <div className="products-header">
        <h1>Administrar Productos</h1>
        <p>Gestiona el catálogo de productos</p>
      </div>

      <div className="search-section">
        <input
          type="text"
          placeholder="Buscar productos por nombre o marca..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {brands.length > 0 && (
          <select
            value={brandFilter}
            onChange={e => setBrandFilter(e.target.value)}
            className="status-filter"
          >
            <option value="">Todas las marcas</option>
            {brands.map(b => <option key={b} value={b}>{b}</option>)}
            <option value={NO_BRAND}>{NO_BRAND}</option>
          </select>
        )}
        <button
          className="btn-secondary"
          onClick={() => setMode('brands')}
        >
          <Tag size={16} />
          <span>Marcas</span>
        </button>
        <button
          className="btn-primary"
          onClick={handleNew}
        >
          <Plus size={16} />
          <span>Nuevo Producto</span>
        </button>
      </div>

      <div className="products-stats">
        <div className="stat-item">
          <div className="stat-value">{products.length}</div>
          <div className="stat-label">Total Productos</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">
            {products.reduce((sum, product) => sum + product.stock, 0)}
          </div>
          <div className="stat-label">Stock Total</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{brands.length}</div>
          <div className="stat-label">Marcas</div>
        </div>
      </div>

      <div className="products-grid">
        {visibleProducts.map(product => (
          <div key={product.id} className="product-card management-card">
            <div className="product-image-wrapper">
              <ProductImage src={product.image} alt={product.name} className="product-image" />
            </div>
            <div className="product-info">
              {product.brand && <div className="product-brand">{product.brand}</div>}
              <div className="product-name">{product.name}</div>
              <div className="product-price">${product.price.toFixed(2)}</div>
              <div className="product-stock">Stock: {product.stock}</div>
            </div>
            <div className="product-actions">
              <button
                className="btn-secondary btn-small"
                onClick={() => handleEdit(product)}
                title="Editar producto"
              >
                <Edit size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {visibleProducts.length === 0 && (
        <div className="no-products">
          <div className="no-products-icon">
            <Package size={48} />
          </div>
          <h3>No hay productos</h3>
          <p>
            {products.length === 0
              ? 'Comienza agregando tu primer producto al catálogo.'
              : 'No se encontraron productos con esos filtros.'}
          </p>
          {products.length === 0 && (
            <button className="btn-primary" onClick={handleNew}>
              <Plus size={16} />
              <span>Agregar Producto</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default ProductManagement;
